import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
    FormControlLabel, FormGroup, Paper, Snackbar, Switch, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import { useTranslation } from 'react-i18next';
import permissionGroupService from '../services/PermissionGroupService';
import HelpButton from './HelpButton';
import { permissionGroups_layout, permissions as permissionRegistry } from '../appConfig';

const s = (v) => String(v ?? '').trim();
const uniq = (list) => {
    const out = [], seen = new Set();
    (Array.isArray(list) ? list : []).forEach((v) => { const x = s(v); if (x && !seen.has(x)) { seen.add(x); out.push(x); } });
    return out;
};

function buildLayout(registry) {
    const known = registry && Object.keys(registry).length ? registry : permissionRegistry;
    const groups = permissionGroups_layout.map((g) => ({
        key: g.key,
        options: g.permissions.filter((k) => k in known).map((k) => ({ key: k, description: s(known[k]) })),
    })).filter((g) => g.options.length);
    const placed = new Set(permissionGroups_layout.flatMap((g) => g.permissions));
    const rest = Object.keys(known).filter((k) => !placed.has(k));
    if (rest.length) groups.push({ key: 'other', options: rest.map((k) => ({ key: k, description: s(known[k]) })) });
    return groups;
}

function GroupDialog({ open, mode, initial, layout, onClose, onSubmit, submitting }) {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [active, setActive] = useState(true);
    const [selected, setSelected] = useState([]);
    const isSystem = Boolean(initial?.systemGroup);

    useEffect(() => {
        if (!open) return;
        const g = initial || {};
        setName(s(g.name)); setDescription(s(g.description));
        setActive(g.active === undefined ? true : Boolean(g.active));
        setSelected(uniq(g.permissions));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initial?.groupId]);

    const selectedSet = useMemo(() => new Set(selected), [selected]);
    const toggle = (k) => setSelected((prev) => {
        const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return Array.from(n);
    });

    const submit = () => onSubmit({ name: s(name), description: s(description), active, permissions: uniq(selected) });

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{mode === 'edit' ? t('permissionGroups.edit', 'Edit Permission Group') : t('permissionGroups.new', 'New Permission Group')}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 3, overflowY: 'visible' }}>
                <TextField label={t('permissionGroups.name', 'Name')} value={name} onChange={(e) => setName(e.target.value)} required fullWidth disabled={submitting} sx={{ mt: 0.5 }} />
                <TextField label={t('permissionGroups.description', 'Description')} value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline minRows={2} disabled={submitting} />
                <FormControlLabel control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} disabled={submitting} />} label={t('permissionGroups.active', 'Active')} />
                {isSystem ? <Alert severity="info" sx={{ py: 0.25 }}>{t('permissionGroups.systemHint', 'Built-in group. Permissions can be changed, but it cannot be deleted.')}</Alert> : null}
                <Box>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('permissionGroups.permissions', 'Permissions')}</Typography>
                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: 320, overflow: 'auto', px: 1.5, py: 1 }}>
                        {layout.map((grp, i) => (
                            <Box key={grp.key}>
                                {i > 0 ? <Divider sx={{ my: 1 }} /> : null}
                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                    {t(`permissionGroups.layout.${grp.key}`, grp.key)}
                                </Typography>
                                <FormGroup>
                                    {grp.options.map((o) => (
                                        <Tooltip key={o.key} title={o.description || ''} placement="right" arrow>
                                            <FormControlLabel
                                                control={<Switch checked={selectedSet.has(o.key)} onChange={() => toggle(o.key)} disabled={submitting} />}
                                                label={t(`permissionGroups.permissionLabels.${o.key}`, o.key)}
                                            />
                                        </Tooltip>
                                    ))}
                                </FormGroup>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={submitting}>{t('permissionGroups.cancel', 'Cancel')}</Button>
                <Button variant="contained" onClick={submit} disabled={!s(name) || submitting}>{mode === 'edit' ? t('permissionGroups.update', 'Update') : t('permissionGroups.create', 'Create')}</Button>
            </DialogActions>
        </Dialog>
    );
}

function PermissionGroups() {
    const { t } = useTranslation();
    const [groups, setGroups] = useState([]);
    const [registry, setRegistry] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [forbidden, setForbidden] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('create');
    const [editing, setEditing] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const layout = useMemo(() => buildLayout(registry), [registry]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            let data = await permissionGroupService.getGroups();
            if (!Array.isArray(data) || data.length === 0) data = await permissionGroupService.seedGroups();
            setGroups(Array.isArray(data) ? data : []);
            setForbidden(false);
        } catch (e) {
            if (String(e?.message || '').includes('403')) setForbidden(true);
            else setSnackbar({ open: true, message: t('permissionGroups.failedLoad', 'Failed to load permission groups'), severity: 'error' });
        } finally { setLoading(false); }
    }, [t]);

    useEffect(() => {
        load();
        permissionGroupService.getPermissionRegistry().then((d) => setRegistry(d && typeof d === 'object' ? d : {})).catch(() => setRegistry({}));
    }, [load]);

    const submitDialog = async (payload) => {
        setSaving(true);
        try {
            if (dialogMode === 'edit') {
                await permissionGroupService.updateGroup(editing?.groupId, { ...payload, groupId: editing?.groupId });
                setSnackbar({ open: true, message: t('permissionGroups.updated', 'Permission group updated'), severity: 'success' });
            } else {
                await permissionGroupService.createGroup(payload);
                setSnackbar({ open: true, message: t('permissionGroups.created', 'Permission group created'), severity: 'success' });
            }
            setDialogOpen(false); await load();
        } catch (e) {
            setSnackbar({ open: true, message: `${t('permissionGroups.failedSave', 'Failed to save')} ${e.message || ''}`.trim(), severity: 'error' });
            throw e;
        } finally { setSaving(false); }
    };

    const toggleActive = async (g) => {
        setSaving(true);
        try { await permissionGroupService.updateGroup(g.groupId, { groupId: g.groupId, active: !g.active }); await load(); }
        catch { setSnackbar({ open: true, message: t('permissionGroups.failedSave', 'Failed to save'), severity: 'error' }); }
        finally { setSaving(false); }
    };

    const remove = async (g) => {
        if (!window.confirm(t('permissionGroups.deleteConfirm', 'Delete {{name}}?', { name: g.name || g.groupId }))) return;
        setSaving(true);
        try { await permissionGroupService.deleteGroup(g.groupId); setSnackbar({ open: true, message: t('permissionGroups.deleted', 'Deleted'), severity: 'success' }); await load(); }
        catch (e) { setSnackbar({ open: true, message: `${t('permissionGroups.failedDelete', 'Failed to delete')} ${e.message || ''}`.trim(), severity: 'error' }); }
        finally { setSaving(false); }
    };

    const head = { fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.07em', textTransform: 'uppercase', color: '#8a9bb5', backgroundColor: '#131926', borderBottom: '2px solid rgba(40,123,214,0.35)', py: 1.5 };

    if (forbidden) {
        return <Box sx={{ px: 1 }}><Alert severity="error" icon={<LockPersonIcon />}>{t('permissionGroups.adminOnly', 'Only administrators can manage permission groups.')}</Alert></Box>;
    }

    const roleBadge = (g) => g.roleDefault ? (
        <Chip size="small" label={t(`permissionGroups.base.${g.roleDefault}`, `${g.roleDefault} base`)} sx={{ ml: 1, height: 20, fontSize: '0.65rem', bgcolor: 'rgba(76,175,80,0.14)', color: '#2e7d32' }} />
    ) : null;

    return (
        <Box sx={{ px: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 4, height: 28, borderRadius: 2, bgcolor: '#287bd6' }} />
                <Box sx={{ mr: 'auto' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, color: '#1e2030' }}>{t('permissionGroups.title', 'Permission Groups')}</Typography>
                        <HelpButton />
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', color: '#6b7a99' }}>{t('permissionGroups.subtitle', 'Shared across all clients · {{num}} groups', { num: groups.length })}</Typography>
                </Box>
                <Button variant="contained" onClick={() => { setDialogMode('create'); setEditing(null); setDialogOpen(true); }} disabled={loading || saving}
                    sx={{ borderRadius: '8px', fontWeight: 600, background: 'linear-gradient(135deg, #287bd6 0%, #1f6fca 100%)' }}>
                    {t('permissionGroups.new', 'New Permission Group')}
                </Button>
            </Box>

            {loading ? <Alert severity="info">{t('permissionGroups.loading', 'Loading…')}</Alert> : null}

            {!loading && groups.length === 0 ? (
                <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px dashed rgba(0,0,0,0.13)', borderRadius: '10px' }}>
                    <Typography sx={{ color: '#6b7a99' }}>{t('permissionGroups.none', 'No permission groups found.')}</Typography>
                </Paper>
            ) : (
                <TableContainer component={Paper} elevation={0} sx={{ border: '1.5px solid rgba(0,0,0,0.13)', borderRadius: '10px', overflow: 'hidden' }}>
                    <Table size="small">
                        <TableHead><TableRow>
                            <TableCell sx={head}>{t('permissionGroups.name', 'Name')}</TableCell>
                            <TableCell sx={head}>{t('permissionGroups.description', 'Description')}</TableCell>
                            <TableCell sx={head}>{t('permissionGroups.permissions', 'Permissions')}</TableCell>
                            <TableCell sx={head}>{t('permissionGroups.assignable', 'User-assignable')}</TableCell>
                            <TableCell sx={head}>{t('permissionGroups.active', 'Active')}</TableCell>
                            <TableCell align="right" sx={head}>{t('permissionGroups.actions', 'Actions')}</TableCell>
                        </TableRow></TableHead>
                        <TableBody>
                            {groups.map((g) => (
                                <TableRow key={g.groupId} sx={{ '&:hover td': { backgroundColor: 'rgba(40,123,214,0.18)' } }}>
                                    <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600, color: '#1e2030' }}>
                                        {g.name || g.groupId}
                                        {g.systemGroup ? <Chip size="small" label={t('permissionGroups.builtin', 'Built-in')} sx={{ ml: 1, height: 20, fontSize: '0.65rem', bgcolor: 'rgba(40,123,214,0.12)', color: '#1f6fca' }} /> : null}
                                        {roleBadge(g)}
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 320, color: '#5a6275', fontSize: '0.85rem' }}>{g.description || ''}</TableCell>
                                    <TableCell sx={{ maxWidth: 320 }}>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {uniq(g.permissions).length === 0 ? <Typography sx={{ fontSize: '0.8rem', color: '#8a9bb5' }}>{t('permissionGroups.noPerms', 'None')}</Typography>
                                                : uniq(g.permissions).map((k) => <Chip key={k} size="small" label={t(`permissionGroups.permissionLabels.${k}`, k)} sx={{ height: 22, fontSize: '0.7rem' }} />)}
                                        </Box>
                                    </TableCell>
                                    <TableCell>{g.assignable ? t('permissionGroups.yes', 'Yes') : t('permissionGroups.no', 'No')}</TableCell>
                                    <TableCell><Switch size="small" checked={Boolean(g.active)} onChange={() => toggleActive(g)} disabled={saving} /></TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        <Button size="small" onClick={() => { setDialogMode('edit'); setEditing(g); setDialogOpen(true); }} disabled={saving} sx={{ fontWeight: 600, color: '#1e2030' }}>{t('permissionGroups.editBtn', 'Edit')}</Button>
                                        <Tooltip title={g.systemGroup ? t('permissionGroups.cannotDelete', 'Built-in groups cannot be deleted. Deactivate instead.') : ''} placement="top" arrow>
                                            <span><Button size="small" onClick={() => remove(g)} disabled={saving || Boolean(g.systemGroup)} sx={{ fontWeight: 600, color: '#287bd6' }}>{t('permissionGroups.deleteBtn', 'Delete')}</Button></span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <GroupDialog open={dialogOpen} mode={dialogMode} initial={editing} layout={layout}
                onClose={() => { if (!saving) setDialogOpen(false); }} onSubmit={submitDialog} submitting={saving} />
            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((x) => ({ ...x, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar((x) => ({ ...x, open: false }))}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
}

export default PermissionGroups;
